import { z } from "zod";

/**
 * Billing contract stubs (M0 ships types only — no Stripe integration or
 * quota enforcement here, per M0.md's explicit Out-of-scope. M7 Accounts,
 * Freemium Gate & Billing implements behavior). `CheckoutRequest` carries
 * `anon_session_id` so M7's anon-map claim flow can reuse the M0
 * anon-session migration hook without a new mechanism.
 */

export const CURRENT_BILLING_SCHEMA_VERSION = 1;
export const SUPPORTED_BILLING_SCHEMA_VERSIONS = [1];

export const BillingCycleSchema = z.enum(["monthly", "annual"]);
export type BillingCycle = z.infer<typeof BillingCycleSchema>;

export const CheckoutRequestSchema = z.object({
  schema_version: z.literal(CURRENT_BILLING_SCHEMA_VERSION),
  account_id: z.string().optional(),
  anon_session_id: z.string().optional(),
  // Optional so a generic "Upgrade" button (nav, Pro-upsell modal) can go
  // straight to Stripe checkout without knowing the plan: the route resolves the
  // default paid tier + default cycle (monthly) when these are omitted. The
  // pricing/billing cards still send explicit values so their toggle works.
  plan_id: z.string().optional(),
  billing_cycle: BillingCycleSchema.optional(),
  intended_action: z.string().optional(),
  trigger_event: z.string().optional(),
  quota_type: z.string().optional(),
  checkout_attempt_id: z.string().uuid().optional(),
  paywall_lead_id: z.string().uuid().optional(),
  success_url: z.string(),
  cancel_url: z.string(),
});
export type CheckoutRequest = z.infer<typeof CheckoutRequestSchema>;

export const NormalizedBillingEventTypeSchema = z.enum([
  "checkout.session.completed",
  "customer.subscription.created",
  "customer.subscription.updated",
  "customer.subscription.deleted",
  "invoice.paid",
  "invoice.payment_failed",
  "charge.refunded",
]);
export type NormalizedBillingEventType = z.infer<typeof NormalizedBillingEventTypeSchema>;

export const NormalizedBillingEventSchema = z.object({
  schema_version: z.literal(CURRENT_BILLING_SCHEMA_VERSION),
  event_id: z.string(),
  type: NormalizedBillingEventTypeSchema,
  account_id: z.string().optional(),
  customer_id: z.string(),
  subscription_id: z.string().optional(),
  occurred_at: z.string(),
  raw_provider: z.literal("stripe"),
  idempotency_key: z.string(),
});
export type NormalizedBillingEvent = z.infer<typeof NormalizedBillingEventSchema>;
